/**
 * Provider label helpers shared by the settings screens.
 *
 * The catalog carries both names, so the choice belongs to the *view*, not to the API: this function is the one place
 * that decides which name a Persian or English page shows, and the wizard reuses it.
 *
 * Since ADR-0019 the only credential is the platform provider profile an administrator defined, and it carries no
 * catalogue entry to read a name from, so the family labels live here as data and a family the tables do not know still
 * renders as its enum value rather than disappearing from a picker.
 */

import type { AiProviderOption, SelectableProviderProfile } from '@/types/api';

/**
 * Returns the provider name for the active interface language.
 *
 * @param provider Catalog entry.
 * @param language Current i18next language tag.
 * @returns The localized display name.
 */
export function providerDisplayName(provider: AiProviderOption, language: string): string {
  return language.startsWith('en') ? provider.displayNameEn : provider.displayNameFa;
}

/**
 * Returns the provider name for a stored credential.
 *
 * A credential may name a provider that the catalog no longer offers (a decommissioned family, or a catalog that
 * failed to load). Showing the raw enum value is the honest fallback: it tells the user exactly which record they are
 * looking at instead of hiding the row.
 *
 * @param kind Provider kind stored on the credential.
 * @param providers Catalog, possibly missing an entry for `kind`.
 * @param language Current i18next language tag.
 * @returns The localized display name, or the kind itself when the catalog has no entry.
 */
export function providerDisplayNameForKind(
  kind: string,
  providers: readonly AiProviderOption[],
  language: string,
): string {
  const provider = providers.find((candidate) => candidate.kind === kind);

  return provider ? providerDisplayName(provider, language) : kind;
}

/**
 * Returns the provider family name when the catalog has not loaded it.
 *
 * The co-pilot and merge panels only ever see the account's preferred pair, so they can run before the catalog answers —
 * and a catalogue that failed to load must not leave a blank where the family belongs. The static tables therefore have
 * to be reachable without an `AiProviderOption`, which is what this entry point is for.
 *
 * @param kind Provider family.
 * @param language Current i18next language tag.
 * @returns The localized family name, or the kind itself when the tables do not know it.
 */
export function providerKindLabel(kind: string, language: string): string {
  return language.startsWith('en') ? providerKindLabelEn(kind) : providerKindLabelFa(kind);
}

/**
 * Returns the option label of a platform provider profile (ADR-0019 §D3).
 *
 * A profile is identified by the name the administrator gave it, so the name is what the picker shows — but a list of
 * names alone hides the two facts that decide between two similar connections, which are the provider family and the model
 * the profile serves. Both are appended when they exist, and a profile without a model shows no model rather than an empty
 * separator.
 *
 * @param profile Profile as the selectable list publishes it.
 * @param language Current i18next language tag.
 * @returns The label shown in the credential picker.
 */
export function providerProfileLabel(
  profile: Pick<SelectableProviderProfile, 'displayName' | 'providerKind' | 'modelId'>,
  language: string,
): string {
  const provider = language.startsWith('en')
    ? providerKindLabelEn(profile.providerKind)
    : providerKindLabelFa(profile.providerKind);

  return profile.modelId === null
    ? `${profile.displayName} · ${provider}`
    : `${profile.displayName} · ${provider} · ${profile.modelId}`;
}

/**
 * Returns the Persian name of a provider family, falling back to the enum value for a family the catalogue has not named.
 *
 * @param kind Provider family.
 * @returns The Persian label.
 */
function providerKindLabelFa(kind: string): string {
  return PROVIDER_KIND_LABELS_FA[kind] ?? kind;
}

/**
 * Returns the English name of a provider family, falling back to the enum value.
 *
 * @param kind Provider family.
 * @returns The English label.
 */
function providerKindLabelEn(kind: string): string {
  return PROVIDER_KIND_LABELS_EN[kind] ?? kind;
}

/** Persian names of the provider families the catalog publishes; a new family renders as its enum value until added. */
const PROVIDER_KIND_LABELS_FA: Readonly<Record<string, string>> = {
  OpenAi: 'اوپن‌ای‌آی',
  Anthropic: 'آنتروپیک',
  GoogleGemini: 'گوگل جمینی',
  Ollama: 'اولاما',
  CustomOpenAiCompatible: 'سازگار با OpenAI',
};

/** English names of the same families. */
const PROVIDER_KIND_LABELS_EN: Readonly<Record<string, string>> = {
  OpenAi: 'OpenAI',
  Anthropic: 'Anthropic',
  GoogleGemini: 'Google Gemini',
  Ollama: 'Ollama',
  CustomOpenAiCompatible: 'OpenAI-compatible',
};
